import axios, { AxiosError } from 'axios';
import { apiConfig } from '../utils/apiConfig';

export interface TelefonlisteEntry {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  isHeader: boolean;
  image: string;
}

export interface TelefonlisteResponse {
  entries: TelefonlisteEntry[];
  updatedAt: number | null;
}

type NullableToken = string | null | undefined;

const API_URL = apiConfig.apiUrl;

const buildHeaders = (token?: NullableToken) => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const toTelefonlisteResponse = (data: any): TelefonlisteResponse => {
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  const updatedAt = typeof data?.updatedAt === 'number' ? data.updatedAt : null;
  return { entries, updatedAt };
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>;
    const data = axiosError.response?.data;
    if (data && typeof data === 'object') {
      if (typeof data.error === 'string' && data.error.trim().length > 0) {
        return data.error;
      }
      if (typeof data.message === 'string' && data.message.trim().length > 0) {
        return data.message;
      }
    }
    if (typeof axiosError.message === 'string' && axiosError.message.trim().length > 0) {
      return axiosError.message;
    }
  } else if (error instanceof Error) {
    return error.message;
  }
  return fallback;
};

const telefonlisteService = {
  async fetch(token?: NullableToken): Promise<TelefonlisteResponse> {
    try {
      const response = await axios.get(API_URL, {
        params: { action: 'getTelefonliste' },
        headers: buildHeaders(token)
      });
      return toTelefonlisteResponse(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Telefonliste konnte nicht geladen werden.'));
    }
  },

  async update(entries: TelefonlisteEntry[], token: string): Promise<TelefonlisteResponse> {
    try {
      const response = await axios.post(
        API_URL,
        {
          action: 'updateTelefonliste',
          entries
        },
        {
          headers: buildHeaders(token)
        }
      );
      return toTelefonlisteResponse(response.data);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Telefonliste konnte nicht gespeichert werden.'));
    }
  },

  async uploadContactImage(contactId: string, file: File, token: string): Promise<{ imageUrl: string }> {
    try {
      const formData = new FormData();
      formData.append('action', 'uploadContactImage');
      formData.append('contactId', contactId);
      formData.append('image', file);

      const response = await axios.post(API_URL, formData, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Bild-Upload fehlgeschlagen.');
      }

      return { imageUrl: response.data.imageUrl };
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Kontaktbild konnte nicht hochgeladen werden.'));
    }
  }
};

export default telefonlisteService;
